import { useState } from 'react';
import { Link } from 'react-router';

import { CheckIcon, CloseIcon, IconSizes } from '@treneo/shared/icons';
import { Box, IconButton, Typography } from '@treneo/shared/ui';

import type { Notification } from '../../types/notification.types';

type Props = Readonly<{
  notification: Notification;
  detailsLinkLabel: string;
}>;

export const NotificationItem = ({ notification, detailsLinkLabel }: Props) => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const handleAccept = async () => {
    if (!notification.onAccept || isAccepting) {
      return;
    }
    setIsAccepting(true);
    try {
      await notification.onAccept();
      setIsAccepting(false);
    } catch (e) {
      setIsAccepting(false);
      throw e;
    }
  };

  const handleReject = async () => {
    if (!notification.onReject || isRejecting) {
      return;
    }
    setIsRejecting(true);
    try {
      await notification.onReject();
      setIsRejecting(false);
    } catch (e) {
      setIsRejecting(false);
      throw e;
    }
  };

  const hasActions =
    (notification.acceptLabel != null && notification.onAccept != null) ||
    (notification.rejectLabel != null && notification.onReject != null);
  const isActionDisabled = isAccepting || isRejecting;

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant='subtitle2' component='span' display='block'>
        {notification.title}
      </Typography>
      <Typography variant='body2' color='text.secondary' component='span' display='block'>
        {notification.description}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        {notification.detailsUrl && (
          <Link to={notification.detailsUrl}>
            <Typography variant='button' color='primary' component='span'>
              {detailsLinkLabel}
            </Typography>
          </Link>
        )}
        {hasActions && (
          <Box component='span' sx={{ display: 'inline-flex', gap: 0.5, ml: 'auto' }}>
            {notification.acceptLabel && notification.onAccept && (
              <IconButton
                size='small'
                aria-label={notification.acceptLabel}
                onClick={handleAccept}
                disabled={isActionDisabled}
              >
                <CheckIcon fontSize={IconSizes.small} />
              </IconButton>
            )}
            {notification.rejectLabel && notification.onReject && (
              <IconButton
                size='small'
                aria-label={notification.rejectLabel}
                onClick={handleReject}
                disabled={isActionDisabled}
              >
                <CloseIcon fontSize={IconSizes.small} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
